import type { WorkflowDefinition } from "../types";

/**
 * W16 — Find and fix what stops AI from citing the site.
 *
 * `shared`: an audit is about the whole site, and the whole team is fixing the
 * same one. `explicit`, because it is a piece of work with a start date: an
 * account that had audited its site last spring has not started this pass.
 *
 * The closing step wants "a second audit, after the first one" and the
 * vocabulary has no way to say that directly, so it pairs a start-anchored
 * `since` with a count of two. See the comment on the step itself.
 */
export const citationBlockersWorkflow: WorkflowDefinition = {
	id: "citation-blockers",
	wNumber: 16,
	title: "Find and fix what stops AI from citing your site",
	outcome:
		"Fix the things that stop AI reading your pages, then prove the fix landed.",
	audiences: ["inhouse", "content"],
	uses: ["AI Readiness Audit", "Projects", "Citation Optimizer"],
	scope: "property",
	concurrency: "shared",
	progress: "explicit",
	// The audit crawls a whole site, so it needs a subject that owns one.
	isApplicable: (mode) => mode.showSiteAudit,
	articleSlug: "how-to-find-and-fix-ai-citation-blockers",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "audit",
			title: "Run an audit of the whole site",
			page: "AI Readiness Audit",
			route: "/site-audit",
			criterion: "You've completed an audit since you started.",
			why: "The audit scores every page on the seven things that decide whether AI can read and quote it. Guessing which pages have a problem wastes weeks.",
			docsHref: "/docs/dashboards/ai-readiness-audit",
			tasks: [
				"Run an audit across the whole site, or a filtered part of it",
				"Wait for the crawl to finish, then read the site score",
				"Skim the seven dimension scores before the page list",
			],
			complete: { kind: "since", event: "siteAuditCompleted" },
			spend: { kind: "siteAudit", pages: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Audits completed",
							value: state.property.audits.completedCount,
							target: 1,
						}
					: null,
		},
		{
			id: "triage",
			title: "Sort the findings by what they cost you",
			page: "AI Readiness Audit",
			route: "/dashboard/site-audit",
			criterion: "You've picked the findings worth fixing.",
			why: "An audit hands you a hundred things. Scoring weight and page importance decide which handful actually block a citation.",
			docsHref: "/docs/dashboards/ai-readiness-audit",
			tasks: [
				"Sort by scoring weight, not by the number of findings",
				"Fix critical findings before warnings",
				"Split the list into edits you can make and engineering tickets",
			],
			// Judgement call: nothing watches "you decided what matters".
			complete: { kind: "manual" },
		},
		{
			id: "project-from-audit",
			title: "Track the fixes as a project",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've created a project since you started.",
			why: "The audit can turn your priority pages into an SEO project in one step. That is how the fixes get measured rather than forgotten.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Use the create-project shortcut on the audit",
				"Add the page group holding your priority pages",
				"Set a goal on that group so citations are counted",
			],
			complete: { kind: "since", event: "projectCreated" },
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active projects",
							value: state.property.activeProjectCount,
							target: 1,
						}
					: null,
		},
		{
			id: "optimize-priority-pages",
			title: "Sharpen the priority pages against a real search",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've completed a score since you started.",
			why: "The audit says a page is generally weak. The Citation Optimizer says whether it would win one specific search.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Score a fixed page against the search it should win",
				"Read the failing checks, then generate a rewrite",
				"Publish through your CMS and score again for free",
			],
			complete: { kind: "since", event: "pipelineRunCompleted" },
			spend: { kind: "citationRewrite" },
			signal: ({ state }) => {
				const score = state.property?.pipeline.latestScore ?? null;
				if (score === null) {
					return null;
				}
				return {
					kind: "score",
					label: "Citation score",
					value: score,
					target: 70,
				};
			},
		},
		{
			id: "re-audit",
			title: "Audit again and compare the scores",
			page: "AI Readiness Audit",
			route: "/site-audit",
			criterion: "You've completed a second audit.",
			why: "A fix that looks right in the CMS does not count until the next crawl reads it. Compare the same pages before and after.",
			docsHref: "/docs/dashboards/ai-readiness-audit",
			tasks: [
				"Run an audit again over the same pages",
				"Compare the page scores directly",
				"Chase anything that did not move",
			],
			// What this step means is "a *second* audit, after the first one",
			// and there is no way to say that: `since` alone would be checked
			// off by the opening audit. Pairing it with a count of two is the
			// closest the vocabulary gets, and the `since` arm is what keeps
			// an account that audited twice last year from starting here done.
			complete: {
				kind: "all",
				of: [
					{ kind: "since", event: "siteAuditCompleted" },
					{ kind: "count", key: "audits.completedCount", gte: 2 },
				],
			},
			spend: { kind: "siteAudit", pages: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Audits completed",
							value: state.property.audits.completedCount,
							target: 2,
						}
					: null,
		},
	],
};
